/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import MsDyn365, { IImageData, Image, IImageSettings } from '@msdyn365-commerce/core';
import { ArrayExtensions, generateImageUrl } from '@msdyn365-commerce-modules/retail-actions';
import { getPayloadObject, getTelemetryAttributes, getTelemetryObject, IPayLoad, ITelemetryContent, Module, Node, onTelemetryClick } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';

import { INavigationMenuViewProps } from '@msdyn365-commerce-modules/navigation-menu/src/modules/navigation-menu/./navigation-menu';
import { IMenuItemData } from '@msdyn365-commerce-modules/navigation-menu/src/modules/navigation-menu/./navigation-menu.data';
import { getCategoryUrl } from '@msdyn365-commerce-modules/retail-actions';
import { CategoryHierarchy } from '@msdyn365-commerce/commerce-entities';
import { IActionContext } from '@msdyn365-commerce/core';
import { TextValueTranslation } from '@msdyn365-commerce/retail-proxy';
import { ICmsNavItemsData, ISubMenusData, navigationMenuSource } from '../definition-extensions/navigation-menu.ext.props.autogenerated';

interface INavigationState {
    parentMenu?: number;
    activeMenu?: number;
    categoryImage?: IImageData[] | null;
    mobileViewLabelText?: string;
    mobileViewLabelLink?: string;
    categoryImageAltText: string;
    scrollClass: boolean;
    /* Uptail Customization - START
    when mouse moves on any submneu item, currentcategory shows its option at ID if at level 2 and previous category ID (which is actually its parent ID) if at level 3 */
    currentCategory?: number | undefined;
    // checks whether back button is clicked or not on mobile menu
    isBackBtnClicked?: boolean;
    categoryImageLink?: string;
    /* Uptail Customization - END */
}

/**
 *
 * NavigationMenuView component.
 * @extends {React.PureComponent<INavigationMenuViewProps>}
 */
export class NavigationMenuView extends React.PureComponent<INavigationMenuViewProps, INavigationState> {
    private static isBackTrack: boolean = false;
    private currentLevel: number = 0;

    /* Uptail Customization - START*/
    private supportedLevel: number = 3;
    private static CMSItemIdInit: number = 1000;
    private static RSItemIdInit: number = 5000;
    private currentLevelMobile: number = 0;
    private menuItemData: IMenuItemData[];
    private isRetail: boolean = true;
    private isCMS: boolean = true;
    private documentRef: Document | undefined;
    /* Uptail Customization - END */

    private readonly menuNode: React.RefObject<HTMLUListElement>;
    private readonly menuButtonsNode: React.RefObject<HTMLUListElement>;
    private readonly telemetryContent: ITelemetryContent;
    private readonly payLoad: IPayLoad;

    constructor(props: INavigationMenuViewProps) {
        super(props);
        this.menuNode = React.createRef();
        this.menuButtonsNode = React.createRef();
        this.state = { activeMenu: undefined, mobileViewLabelText: '', mobileViewLabelLink: '', parentMenu: undefined, scrollClass: false, categoryImageAltText: '' };

        this.menuItemData = this._mapData(); // Uptail Customization
        this._setNavigationSource(props.config.navigationMenuSource as (navigationMenuSource | undefined)); // Uptail Customization

        this._closeSubmenu = this._closeSubmenu.bind(this);
        this.telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry);
        this.payLoad = getPayloadObject('click', this.telemetryContent, '', '');
    }

    public componentDidMount(): void {

        this.documentRef = document;

        if (MsDyn365.isBrowser) {
            document.body.addEventListener('mousedown', this._handleClickOutside);
            document.body.addEventListener('focusout', this._handleFocusOutside);

            /* Uptail Customization - START - Setting mousehover to false to not display submenu by defualt */
            document.body.addEventListener('mousedown', this._handleMouseHover(undefined));
            document.body.addEventListener('mousedown', this._handleLinkClick(undefined));
            /* Uptail Customization - END */
        }

        /* Uptail Customization - START*/
        /* Sticky Navigation */
        //window.addEventListener('scroll', () => {
        //    this._mainMenuScroll(this.state.scrollClass);
        //});
        /* Uptail Customization - END*/
    }

    public componentWillUnmount(): void {
        if (MsDyn365.isBrowser) {
            document.body.removeEventListener('mousedown', this._handleClickOutside, false);
            document.body.removeEventListener('focusout', this._handleFocusOutside, false);

            /* Uptail Customization - START - Setting mousehover to false to not display submenu while unmounting component */
            document.body.removeEventListener('mousedown', this._handleMouseHover(undefined));
            document.body.removeEventListener('mousedown', this._handleLinkClick(undefined));
            /* Uptail Customization - END */
        }

    }

    public render(): JSX.Element | null {
        const { isMobileView, MenuList, MobileBackButton, MobileDescriptionContainer, MobileDescriptionLabel, Navigation, resources: { backButtonAriaLabel } } = this.props;
        this.currentLevel = 1;

        return (
            <Module {...Navigation} className={classnames(Navigation.className, isMobileView && this.state.activeMenu !== undefined ? 'child' : 'parent', this.state.scrollClass ? 'sticky-nav' : '')}>
                {
                    isMobileView && this.state.activeMenu &&
                    <Node {...MobileDescriptionContainer} ref={this.menuButtonsNode}>
                        <Node className={'nav-menu_back-button_container'} onClick={ this._handleGoBack()}>
                            <Node {...MobileBackButton} onClick={null} />
                            <Node {...MobileDescriptionLabel}>
                                {backButtonAriaLabel}
                            </Node>
                        </Node>
                    </Node>
                }
                <Node {...MenuList} ref={this.menuNode} tabIndex='-1'>
                    {isMobileView && this.state.activeMenu !== undefined && this.state.mobileViewLabelText &&
                        <Node {...MobileDescriptionContainer}>
                                { (this.state.mobileViewLabelLink && this.state.mobileViewLabelLink != '')  ?
                                    <Node
                                        {...this.props.Link}
                                        className={MobileDescriptionLabel.className}
                                        href={this.state.mobileViewLabelLink}
                                    >
                                        {this.state.mobileViewLabelText}
                                    </Node>
                                    : <Node {...MobileDescriptionLabel}>
                                        {this.state.mobileViewLabelText}
                                    </Node>
                                }
                        </Node>}
                    {this._renderDisplay()}
                </Node>
            </Module>
        );
    }

    private _renderDisplay(): JSX.Element[] {
        const { ListItem, isMobileView } = this.props;
        const { activeMenu } = this.state;
        const { menuItemData } = this;
        const menuItemList: JSX.Element[] = [];

        if (isMobileView && activeMenu !== undefined && menuItemData.length > 0) {
            let menuItem: IMenuItemData = {};
            for (const menuItemDatum of menuItemData) {
                if (menuItemDatum && menuItemDatum.id === activeMenu) {
                    menuItem = menuItemDatum;
                    !NavigationMenuView.isBackTrack ? this.setState({ parentMenu: undefined }) : this.setState({ parentMenu: undefined, mobileViewLabelText: menuItemDatum.linkText, mobileViewLabelLink: menuItemDatum.linkURL  });
                    break;
                }
                menuItem = this._getFromSubMenu(menuItemDatum) as IMenuItemData;
                if (menuItem && menuItem.id === activeMenu) {
                    break;
                }
            }

            if (menuItem) {
                //(this.currentLevelMobile < this.supportedLevel && this.state.isBackBtnClicked === false) && this.currentLevelMobile++;  //Uptail Customization
                (this.currentLevelMobile < this.supportedLevel && this.state.isBackBtnClicked === false && this.currentLevelMobile == 0) && this.currentLevelMobile++;  //Uptail Customization
                menuItem && menuItemList.push(
                    <Node key={menuItem.id} {...ListItem}>
                        {' '}
                        {this._createMenuItemList(menuItem)}
                        {' '}
                    </Node>
                );
            }
        } else {
            this.currentLevelMobile = 0; //Uptail Customization
            menuItemData.forEach((item: IMenuItemData, index: number) => {
                menuItemList.push(
                    <Node key={index} {...ListItem} className={classnames(ListItem.className, 'nav-menu-category')} >
                        {this._createMenuItemList(item)}
                    </Node>
                );
            });
        }

        return menuItemList;
    }

    private _getFromSubMenu(item?: IMenuItemData): IMenuItemData | null {
        const subMenus = item && item.subMenu;
        if (subMenus && subMenus.length > 0) {
            for (let i = 0; i <= subMenus.length - 1; i++) {
                if (subMenus[i].id === this.state.activeMenu) {
                    !NavigationMenuView.isBackTrack ? this.setState(
                        { parentMenu: item?.id }
                    ) : this.setState(
                        { parentMenu: item && item.id, mobileViewLabelText: subMenus[i].linkText, mobileViewLabelLink: subMenus[i].linkURL }
                    );
                    return subMenus[i];
                }
                const found = this._getFromSubMenu(subMenus[i]);
                if (found) {
                    return found;
                }
            }
        }
        this.props.context.telemetry.error('Submenu content is empty, module wont render.');
        return null;
    }

    private _renderMobileSubMenu(menuItemData: IMenuItemData): JSX.Element | null {
        return (
            <React.Fragment>
                {this._renderButtonMenuItem(menuItemData)}
                {this._renderSubMenu(menuItemData.subMenu, menuItemData.id, undefined, menuItemData.linkText)}
            </React.Fragment>
        );
    }

    /* Uptail Customization - START - Display all option on 3rd level */
    private _mapData(): IMenuItemData[] {
        const { data, config } = this.props;
        const mergedData: IMenuItemData[] = [];
        if (this.isRetail && data && data.categories && data.categories.result && data.categories.result.length > 0) {
            data.categories.result.map((item: CategoryHierarchy, index) => (
                mergedData.push(this._constructMenuItemData(item, undefined, true, index, true))
            ));
        }

        if (this.isCMS && config.cmsNavItems && config.cmsNavItems.length > 0) {
            config.cmsNavItems.map((item: ICmsNavItemsData, idx: number) => (
                mergedData.push(this._constructMenuItemData(undefined, item, false, idx + 1))
            ));
        }
        return this._constructTopMenuItemData(mergedData);
    }

    private _constructTopMenuItemData(mergedData: IMenuItemData[]): IMenuItemData[] {
        const enableTopLevelMenu = this.props.config.enabletopMenu || false;
        if (!enableTopLevelMenu) {
            return mergedData;
        }
        const mergedRootMenuData: IMenuItemData[] = [];
        const topMenu = {
            id: 1,
            linkText: this.props.config.rootMenuNavigation || '...',
            linkURL: '#',
            imageSource: undefined,
            subMenu: mergedData
        } as IMenuItemData;
        mergedRootMenuData.push(topMenu);

        return mergedRootMenuData;
    }

    // tslint:disable-next-line: cyclomatic-complexity
    private _constructMenuItemData(retailItem?: CategoryHierarchy, cmsItem?: ICmsNavItemsData, isRetail?: boolean, idx?: number, addedAllMenu?: boolean): IMenuItemData {
        const locale = this.props.context && this.props.context.request && this.props.context.request.locale ? this.props.context.request.locale : 'en-us';
        const imageSouce = retailItem && retailItem.Images && retailItem.Images[0].Uri ? retailItem.Images[0].Uri : 'empty';
        if (isRetail) {
            return {
                id: NavigationMenuView.RSItemIdInit++,
                linkText: NavigationMenuView.getFriendlyName(locale, retailItem && retailItem.NameTranslations) || '',
                linkURL: retailItem && retailItem.Url,
                imageSource: imageSouce,
                subMenu: this._getNestedChildren(retailItem, imageSouce, addedAllMenu)
            } as IMenuItemData;
        } else {
            return {
                id: NavigationMenuView.CMSItemIdInit++,
                linkText: cmsItem && cmsItem.linkText || '',
                linkURL: cmsItem && cmsItem.linkUrl && cmsItem.linkUrl.destinationUrl || '',
                imageDestinationURL: cmsItem && cmsItem.imageLink && cmsItem.imageLink.destinationUrl || '',
                imageSource: cmsItem && cmsItem.image && cmsItem.image.src || '',
                subMenu: this._getCMSNestedChildren(cmsItem)
            } as IMenuItemData;
        }
    }

    private _getNestedChildren(category?: CategoryHierarchy, imageSourceUrl?: string, addedAllMenu: boolean = false): IMenuItemData[] {
        const nestedChildren: IMenuItemData[] = [];
        if (!category || !category.Children || !category.Children.length) {
            return [];
        }
        if (!this.props.config.enabletopMenu && addedAllMenu) {
            nestedChildren.push({ linkText: this.props.resources.allCategoryMenuText, imageSource: imageSourceUrl, linkURL: getCategoryUrl(category, this.props.context as unknown as IActionContext, {}) });
        }
        category.Children.map((item: CategoryHierarchy, index) => {
            nestedChildren.push(this._constructMenuItemData(item, undefined, true, index, true));
        });
        return nestedChildren;
    }

    private _getCMSNestedChildren(category?: ICmsNavItemsData): IMenuItemData[] {
        const nestedChildren: IMenuItemData[] = [];

        if (!category || !category.subMenus || !category.subMenus.length) {
            return [];
        }

        category.subMenus.map((item: ISubMenusData, idx: number) => {
            nestedChildren.push(this._constructMenuItemData(undefined, item, false, idx));
        });
        return nestedChildren;
    }

    private _setNavigationSource = (sourceType?: navigationMenuSource | undefined): void => {
        switch (sourceType) {
            case navigationMenuSource.retailServerOnly:
                this.isRetail = true;
                this.isCMS = false;
                break;
            case navigationMenuSource.cmsOnly:
                this.isRetail = false;
                this.isCMS = true;
                break;
            case navigationMenuSource.all:
                this.isRetail = true;
                this.isCMS = true;
                break;
            default:
        }
    }
    /* UT Customization - END*/

    /* Uptail Customization - START*/
    private _getSubMenuLength = (menuItemId: number | undefined) => {
        const menuItem = menuItemId && menuItemId.toString();
        const htmlElement = menuItem && this.documentRef && this.documentRef.getElementById(menuItem);
        const menuItemHeight = htmlElement && htmlElement.offsetHeight;
        return menuItemHeight === 0 ? undefined : menuItemHeight;
    }
    /* Uptail Customization - END */

    private _createMenuItemList(menuItemData: IMenuItemData): JSX.Element | null {
        const { isMobileView } = this.props;
        if (menuItemData && menuItemData.subMenu && menuItemData.subMenu.length > 0) {
            if (this.props.isMobileView && this.state.activeMenu !== undefined) {
                return (this._renderSubMenu(menuItemData.subMenu, menuItemData.id, false, menuItemData.linkText));
            } else {
                /* Uptail Customization - START */
                if (isMobileView) {
                    return this._renderMobileSubMenu(menuItemData);
                }
                return (
                    <React.Fragment>
                        {this._renderButtonMenuItem(menuItemData)}
                        <Node tag='div' className={classnames(this.props.className, 'submenu')} id={menuItemData.id} style={{ height: this._getSubMenuLength(menuItemData.id) }}>
                            {this._renderSubMenu(menuItemData.subMenu, menuItemData.id, undefined)}
                        </Node>
                        <Node className='extra-menu-items' tag='div' />
                    </React.Fragment>
                );
            }
            /* Uptail Customization - END */
        } else if (menuItemData && menuItemData.linkText && menuItemData.linkURL && menuItemData.linkURL.length > 0) {
            let updatedMenuItemData: IMenuItemData = menuItemData;
            /* Displayed static menu items as buttons in desktop view and as link menu items in mobile/iPad view */
            if (isMobileView) {
                return (this._renderLinkMenuItem(updatedMenuItemData));
            }
            return (this._renderButtonMenuItem(updatedMenuItemData));
        } else if (menuItemData && menuItemData.linkText && !menuItemData.linkURL) {
            return (this._renderSpanMenuItem(menuItemData, menuItemData.id, true));
        }
        this.props.context.telemetry.error('Navigation menu content is empty, module wont render.');
        return null;
    }

    private _renderSubMenu(subMenus?: IMenuItemData[], activeMenu?: number, IsSubMenu?: boolean, parentName?: string): JSX.Element | null {
        var showOptionAll = undefined;
        var isChild = IsSubMenu;
        const { isMobileView, ListItem, MenuList } = this.props;
        /* Uptail Customization - START */
        /* Added isMobileView condition to check this condition just for mobile to fill submenu by default in case of desktop */
        if (isMobileView && activeMenu && this.state.activeMenu !== activeMenu) {
            this.props.context.telemetry.error('Navigation Active menu content is empty, module wont render.');
            return null;
        }
        /* Uptail Customization - END */
        if (!subMenus || subMenus.length === 0) {
            this.props.context.telemetry.error('Navigation Submenu content is empty, module wont render.');
            return null;
        }
        /* Uptail Customization - START - 02/11/20 */
        let _subMenus: IMenuItemData[];
        if (isChild === undefined && !showOptionAll) {
            if (!isMobileView) {
                // Second level menu will not include option 'All' on desktop
                _subMenus = subMenus && subMenus.filter(subItem => {
                    return subItem.linkText && subItem.linkText.toLowerCase() !== 'all';
                });
            } else {
                // display parentName instead of all in third level on desktop
                let allOptionFound = false;
                _subMenus = subMenus;
                _subMenus && _subMenus.map(submenu => {
                    if (!allOptionFound && submenu.linkText && submenu.linkText.toLowerCase() === 'all') {
                        submenu.linkText = `${submenu.linkText} ${parentName}`;
                        allOptionFound = true;
                    }
                });
            }
        } else {
            // Third level menu
            // on desktop, do not show the all category menu on the third level
            _subMenus = subMenus;
            _subMenus = subMenus && subMenus.filter(subItem => {
                return subItem.linkText && subItem.linkText.toLowerCase() !== 'all';
            });
        }
        /* Uptail Customization - END */
        let levelClassName: string = '';
        const menuOptions = _subMenus && _subMenus.map((option: IMenuItemData, idx: number) => {
            let isActiveOption = option.id !== undefined && this.state.currentCategory === option.id;
            isActiveOption = isActiveOption && ('id' in option); // if any option doesn't have id value, its submenu will not be created
            const hasOptions = option.subMenu && option.subMenu.length > 0 || false;
            const renderChildMenu = isMobileView ? hasOptions : isActiveOption;

            let menuItem: JSX.Element | null;
            if (hasOptions && isMobileView && this.currentLevelMobile < this.supportedLevel - 1) {
                menuItem = this._renderButtonMenuItem(option, activeMenu, idx);
            } else {
                menuItem = (option.linkURL
                    ? this._renderLinkMenuItem(option, idx, activeMenu, isChild, parentName)
                    : this._renderSpanMenuItem(option)
                );
            }
            let subMenu;
            if (renderChildMenu === true) {
                this.currentLevel++;
                if (this.currentLevel <= this.supportedLevel - 1) {
                    levelClassName = `level-${this.currentLevel.toString()}`;
                    /* Last parameter indicated if it is child or not */
                    subMenu = this._renderSubMenu(option.subMenu, isMobileView ? option.id : undefined, true, option.linkText);
                }
            }
            return (
                <>
                    <Node {...ListItem} key={option.id}>
                        {menuItem}
                        {/* Uptail Customization - START */}
                        {isMobileView && subMenu}
                        {/* Following node displays submenu against a menu item when user moves mouse on it on desktop*/}
                        {!isMobileView &&
                            <Node {...MenuList} className={classnames(MenuList.className, 'level-3')}>
                                <Node {...ListItem} key={option.id} id={option.id}>
                                    {subMenu}
                                </Node>
                            </Node>
                        }
                        {/* Uptails Customization - END */}
                    </Node>
                </>
            );
        });

        return (this._renderMenu(levelClassName, menuOptions, activeMenu));
    }
    /* Uptail Customization - end */

    /* UT Customization */
    private _renderButtonMenuItem(option: IMenuItemData, activeMenu?: number, index?: number): JSX.Element | null {

        let showCannedSearches = false;
        const { Button, isMobileView } = this.props;

        if (this.props.context.request.query && this.props.context.request.query.showCannedSearches === 'true') {
            showCannedSearches = true;
        }

        if (isMobileView) {
            return (
                <Node
                    key={index}
                    className={!showCannedSearches && option.linkText === 'Edits' ? 'ms-nav__list__item_hidden' : 'ms-nav__list__item__button'}
                    {...option.linkText === 'Edits' && !showCannedSearches ? null : { ...Button }}
                    onClick={this._handleDropdownToggle(option, activeMenu)}
                    aria-haspopup={true}
                    aria-expanded={this.state.activeMenu && this.state.activeMenu === option.id ? true : false}
                    data-parent={activeMenu}
                >
                    <div style={!showCannedSearches && option.linkText === 'Edits' ? { display: 'none' } : {}}>
                        {option.linkText}
                    </div>
                </Node>
            );
        } else {
            /* Mouse Hover on menuitem displays its submenu till level 3 */
            /* Mouse Click on menuitem will navigate to its category page */
            return (

                <Node
                    className={!showCannedSearches && option.linkText === 'Edits' ? 'ms-nav__list__item_hidden' : 'ms-nav__list__item__button'}
                    {...option.linkText === 'Edits' && !showCannedSearches ? null : { ...Button }} key={index}
                    onMouseEnter={this._handleDropdownToggle(option, activeMenu)}
                    onClick={this._gotoCategoryPage(option.linkURL)}
                    aria-haspopup={true}
                    aria-expanded={this.state.activeMenu && this.state.activeMenu === option.id ? true : false}
                    data-parent={activeMenu}
                >
                    <div style={!showCannedSearches && option.linkText === 'Edits' ? { display: 'none' } : {}}>
                        {option.linkText}
                    </div>
                </Node>
            );
        }

    }

    private _renderLinkMenuItem(option: IMenuItemData, index?: number,
        activeMenu?: number | undefined, _isChild?: boolean | undefined, parentName?: string): JSX.Element | null {
        const { isMobileView } = this.props;
        const linkText = option.linkText ? option.linkText : '';
        this.payLoad.contentAction.etext = linkText;
        const attributes = getTelemetryAttributes(this.telemetryContent, this.payLoad);
        const optionLinkText = (_isChild && linkText === parentName) ? <strong>{linkText}</strong> : linkText;
        /* Uptail Customization - START*/
        if (isMobileView) {
            return (
                <Node
                    {...this.props.Link}
                    key={index}
                    href={option.linkURL}
                    {...attributes}
                    onClick={onTelemetryClick(this.telemetryContent, this.payLoad, linkText)}
                >
                    {optionLinkText}
                </Node>
            );
        } else {
            /* onClick navigates to category page if clicked item is child item (level 3) */
            const onClick = _isChild ? { onClick: this._handleLinkClick(option.linkText) } : undefined;
            return (
                <Node
                    {...this.props.Link}
                    key={index}
                    href={option.linkURL}
                    {...attributes}
                    onMouseOver={this._handleMouseHover(option.id, _isChild)}
                    {...onClick}
                >
                    {optionLinkText}
                    {(!_isChild && option.subMenu && option.subMenu.length > 0) ? <Node className='ms-expand-arrow' /> : undefined}
                </Node>
            );
        }
        /* Uptail Customization - END*/
    }

    /* Uptail Customization - Following function sets value of previous current category in case of level 3 and option.id in case of level 2 */
    private _handleMouseHover = (optionID: number | undefined, _isChild?: boolean | undefined) => () => {
        const _activeItem = _isChild ? this.state.currentCategory : optionID;
        _activeItem !== undefined && this.setState({ currentCategory: _activeItem });
    }

    /* Uptail Customization - onClick navigates to category page if clicked item is child item */
    private _handleLinkClick = (linkURL: string | undefined) => () => {
        this._gotoCategoryPage(linkURL);
    }

    /* Uptail Customization - Following function navigate to category page */
    private _gotoCategoryPage = (categoryPageURL: string | undefined) => () => {
        if (categoryPageURL) {
            window.location.href = categoryPageURL;
        }
    }

    private _renderSpanMenuItem(option: IMenuItemData, index?: number, isParent: boolean = false): JSX.Element | null {
        const { Span } = this.props;
        return (
            <Node key={index} {...Span} onFocus={isParent && this._closeSubmenu}>
                {option.linkText}
            </Node>
        );
    }

    // Uptail Customization - categoryImageLink has been added
    private _renderMenu(level: string, menuOptions: JSX.Element[], currentItem?: number): JSX.Element | null {
        const { DivContainer, MenuList, ImageDivContainer, showCategoryImage, isMobileView } = this.props;
        const categoryImageDisplay = !isMobileView && (showCategoryImage ) && ArrayExtensions.hasElements(this.state.categoryImage) ;
        //const { categoryImage, categoryImageLink } = this.state;
        this.currentLevel = 1;
        if (showCategoryImage) {
            return (
                <Node {...DivContainer} className={DivContainer!.className}>
                    <Node {...MenuList} className={classnames(MenuList.className, level)}>
                        {menuOptions}
                    </Node>
                    {
                        categoryImageDisplay && this.state.categoryImage && this.state.categoryImage.map(item => (
                            <Node {...ImageDivContainer} className={classnames(ImageDivContainer!.className,'menu-item-image')}>
                                { this.state.categoryImage && this._getCategoryImage(item) }
                            </Node>))
                    }
                </Node>
            );
        }
        return (
            <Node {...DivContainer} className={DivContainer!.className}>
                <Node {...MenuList} className={classnames(MenuList.className, level)} id={currentItem}>
                    {menuOptions}
                </Node>
            </Node>
        );
    }

    private readonly _updateCategoryImage = (categoryImageSrc: string, option: IMenuItemData) => () => {
        const linkText = option && option.linkText ? option.linkText : '';
        const promotionalImage: IImageData[] = [{ src: categoryImageSrc, altText: linkText }];

        // Read category and promotional image in one array
        if (ArrayExtensions.hasElements(option.promotionalContent)) {
            option.promotionalContent.map(item => {
                if (item && item.image) {
                    const imageSrc = item.image.src;
                    const promotionalItemImageSettings = item.image.imageSettings;
                    promotionalImage.push({
                        src: imageSrc, altText: item.text, imageSettings: promotionalItemImageSettings,
                        additionalProperties: { linkUrl: item.linkUrl.destinationUrl }
                    });
                }
            });
        }
        this.setState({
            categoryImage: promotionalImage.length > 0 ? promotionalImage : [{ src: 'empty' }],
            categoryImageAltText: linkText
        });
    };

    private readonly _handleDropdownToggle = (data: IMenuItemData, parentId?: number) => () => {
        if (!this.props.isMobileView) {
            this.setState({
                activeMenu: (this.state.activeMenu && this.state.activeMenu === data.id!) ? undefined : data.id!,
                parentMenu: parentId,
                currentCategory: undefined // Uptail Customization parent of third menu to render third level menu
            });
            if (this.props.showCategoryImage) {
                this._updateCategoryImage(data.imageSource!, data)();
            }
        } else {
            (this.currentLevelMobile < this.supportedLevel && this.state.isBackBtnClicked === false) && this.currentLevelMobile++;

            NavigationMenuView.isBackTrack = false;
            this.setState({
                activeMenu: data.id,
                mobileViewLabelText: data.linkText!,
                mobileViewLabelLink: data.linkURL,
                parentMenu: parentId,
                isBackBtnClicked: false // Uptail Customization 
            });

            window.scrollTo({
                top: 0,
                left: 0,
                behavior: 'smooth'
            });
        }

        this._resetFocus();
    };

    private readonly _handleGoBack = () => () => {
        NavigationMenuView.isBackTrack = true;
        /* Uptail Customization - STRAT */
        this.setState({ activeMenu: this.state.parentMenu, isBackBtnClicked: true });
        this.currentLevelMobile--;
        /* Uptail Customization - END */
        this._resetFocus();
    };

    private readonly _resetFocus = () => {
        if (this.props.isMobileView) {
            setTimeout(() => {
                if (this.menuNode && this.menuNode.current) {
                    this.menuNode.current.focus();
                }
            }, 0);
        }
    };

    /**
     * Method to handle click outside of menu.
     * @param event -HTML event.
     */
    private readonly _handleClickOutside = (event: MouseEvent) => {
        if ((this.menuNode.current && !this.menuNode.current.contains(event.target as Node)) && (this.menuButtonsNode.current && !this.menuButtonsNode.current.contains(event.target as Node)) ) {
            this.setState(
                {
                    activeMenu: undefined,
                    mobileViewLabelText: '',
                    mobileViewLabelLink: '',
                    categoryImage: null
                }
            );
        }
    };

    /**
     * Method to handle click outside of menu.
     * @param event -HTML event.
     */
    private readonly _handleFocusOutside = (event: FocusEvent) => {
        if (this.menuNode.current && !this.menuNode.current.contains(event.relatedTarget as Node)) {
            this._closeSubmenu();
        }
    };

    private _closeSubmenu(): void {
        if (!this.props.isMobileView) {
            this.setState({ activeMenu: undefined, categoryImage: null, mobileViewLabelText: '', mobileViewLabelLink: '' });
        }
    }

    public static getFriendlyName(locale: string, nameTranslations?: TextValueTranslation[]): string | undefined {
        let nameTranslation: TextValueTranslation | undefined;
        if (locale && nameTranslations && nameTranslations.length > 0) {
            nameTranslation = nameTranslations.find(item => item.Language!.toLowerCase() === locale.toLowerCase());
        }

        return nameTranslation && nameTranslation.Text;
    }

    /*private _getCategoryImage = (categoryImage: string): React.ReactNode | null => {
        const categoryImageUrl = generateImageUrl(`${categoryImage}`, this.props.context.actionContext.requestContext.apiSettings);
        if (categoryImageUrl !== undefined) {
          const ImageData: IImageData = { src: categoryImageUrl };
          return (
            <Image
              className='ms-nav-image__item'
              {...ImageData}
              gridSettings={this.props.context.request.gridSettings!}
              {//...this.props.config.imageSettings}
              loadFailureBehavior='default'
              role='tabpanel'
              id={`${categoryImage}__categoryImage`}
            />
          );
        }
        return null;
      }
        */      

      private readonly _getCategoryImage = (categoryImage?: IImageData): React.ReactNode | null => {

        if (!categoryImage || !categoryImage.src) {
            return null;
        }

        const categoryImageUrl = generateImageUrl(`${categoryImage.src}`, this.props.context.actionContext.requestContext.apiSettings);
        const defaultImageSettings: IImageSettings = {
            viewports: {
                xs: { q: 'w=300&h=250&m=8', w: 0, h: 0 },
                sm: { q: 'w=300&h=250&m=8', w: 0, h: 0 },
                md: { q: 'w=300&h=250&m=8', w: 0, h: 0 },
                lg: { q: 'w=300&h=250&m=8', w: 0, h: 0 }
            },
            lazyload: true
        };
        if (categoryImageUrl !== undefined) {
            return (
                <Image
                    requestContext={this.props.context.actionContext.requestContext}
                    className='ms-nav-image__item'
                    {...categoryImage}
                    gridSettings={this.props.context.request.gridSettings!}
                    imageSettings={categoryImage.imageSettings ?? (this.props.config.categoryImageSettings ?? defaultImageSettings)}
                    loadFailureBehavior='hide'
                    role='tabpanel'
                    id='categoryImageTag__categoryImage'
                    altText={categoryImage.altText}
                />
            );
        }
        return null;
    };
}

export default NavigationMenuView;
